<script setup lang="ts">
import type { SharingOption } from '../ShareContent';
import type { NavigationTab, Tab } from '../TabsNavigator';
import type { Property, PropertyImage } from '~/core/entities/property';
import type { SectionName } from '~/types/mixpanel';

import { ArrowLeftIcon } from '@radix-icons/vue';
import {
	breakpointsTailwind,
	onClickOutside,
	useClipboard,
	useDebounceFn
} from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useSimilarProperties } from '~/composables/useSimilarProperties';
import GmapClusterRadius from '~/core/service/googleMaps/components/GmapClusterRadius/GmapClusterRadius.vue';
import GmapMap from '~/core/service/googleMaps/components/GmapMap/GmapMap.vue';
import { MixpanelProps } from '~/core/service/mixpanel';
import {
	cn,
	concatUrlPath,
	getPropertyImageAlt,
	hasShortsVideo
} from '~/utils';
import { grammarInflection } from '~~/utils';

import { CopyToastDescription } from '~/components/CopyToastDescription/index';
import { TextLink } from '~/components/ui/text-link';
import { useToast } from '~/components/ui/toast';

import IconMaterialSymbolsShare from '~icons/material-symbols-light/share-outline';

import { FixedFooterContact } from '../FixedFooterContact';
import { Modal } from '../Modal';
import { PropertyPrices } from '../Property/PropertyPrices';
import SimilarPropertiesList from '../PropertyCard/SimilarPropertiesList.vue';
import { TabsNavigator } from '../TabsNavigator';

const { copy } = useClipboard();
const { toast } = useToast();

const props = defineProps<{
	property: Property;
	launchYear: string;
	initialPhotoIndex?: number;
}>();

const emit = defineEmits<{
	(e: 'click-photo', payload: { photoCount: number; photoUrl: string }): void;
	(e: 'click-video', payload: string): void;
	(e: 'click-fixed-contact-button'): void;
}>();

const isOpen = defineModel<boolean>();
const currentTab = defineModel<Tab>('tab', { default: 'images' });

function isFullWidthImage(index: number): boolean {
	return (index + 1) % 3 === 0;
}

function getColSpanClass(index: number): string {
	return isFullWidthImage(index)
		? 'col-span-2 h-[240px]'
		: 'col-span-1 h-[160px]';
}

function getImageDimensions(index: number): {
	width: string;
	sizes: string;
} {
	// On mobile (< md): no sidebar, grid takes full container width
	// On desktop (md+): grid is inside a ~2/3 column next to a sidebar
	// col-span-1: half the grid width (~30-40% of viewport on desktop)
	// col-span-2: full grid width (~60-70% of viewport on desktop)
	// Height is intentionally omitted so the provider resizes by width only,
	// preserving the original aspect ratio. This ensures both landscape and
	// portrait images are always sharp. CSS object-cover handles the visual crop.
	if (isFullWidthImage(index)) {
		return {
			width: '1280',
			sizes: 'xs:100vw sm:100vw md:100vw lg:80vw xl:80vw 2xl:1100px'
		};
	}

	return {
		width: '800',
		sizes: 'xs:50vw sm:50vw md:50vw lg:45vw xl:45vw 2xl:600px'
	};
}

const tabs = computed<NavigationTab<Tab>[]>(
	() =>
		[
			{
				id: 'images',
				label: `${props.property.images.length} ${grammarInflection(props.property.images.length, 'foto', 'fotos')}`,
				active: currentTab.value === 'images'
			},
			props.property.videos?.length
				? {
						id: 'videos',
						label: `${props.property.videos.length} ${grammarInflection(props.property.videos.length, 'vídeo', 'vídeos')}`,
						active: currentTab.value === 'videos'
					}
				: null,
			{
				id: 'map',
				label: 'Mapa',
				active: currentTab.value === 'map'
			},
			{
				id: 'similar',
				label: 'Semelhantes',
				active: currentTab.value === 'similar'
			}
		].filter(Boolean) as NavigationTab<Tab>[]
);

const isCurrentTab = (tab: Tab) => currentTab.value === tab;

const isCarouselOpen = defineModel<boolean>('isCarouselOpen', {
	default: false
});
const currentCarouselItemIndex = ref<number>(0);

const sectionNames: Record<Tab, SectionName> = {
	images: 'photos modal',
	videos: 'videos modal',
	map: 'map modal',
	similar: 'similar properties'
};

const sectionName = computed<SectionName>(
	() => sectionNames[currentTab.value as Tab] || 'photos modal'
);

const imagesEl = ref<HTMLElement | null>(null);
const lastImagesScrollPosition = ref(0);
const shouldRestoreImagesScroll = ref(false);
const { y: imagesScrollPosition } = useScroll(imagesEl);

const {
	public: { ORIGIN }
} = useRuntimeConfig();

const route = useRoute();

const { trackSharePropertyClicked, trackCtaSimilarPropertiesClicked } =
	useEvents();

// Similar properties composable
const basePropertyRef = computed(() => props.property);
const {
	properties: similarProperties,
	isLoading: isSimilarPropertiesLoading,
	hasMore: hasSimilarPropertiesMore,
	isEmpty: isSimilarPropertiesEmpty,
	error: similarPropertiesError,
	loadSimilarProperties,
	loadNextPage: loadSimilarPropertiesNextPage
} = useSimilarProperties(basePropertyRef);

// Show empty state when there's no data OR when there's an error
const shouldShowSimilarPropertiesEmptyState = computed(
	() => isSimilarPropertiesEmpty.value || !!similarPropertiesError.value
);

const showShareContent = ref(false);
const shareAnalyticsExtras = computed(() => ({
	[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(props.property)
}));

const shareUrl = computed<string>(() => concatUrlPath(ORIGIN, route.fullPath));

function toggleShareContent() {
	showShareContent.value = !showShareContent.value;
}

async function shareMobile() {
	const data = {
		title: props.property.ad.title,
		url: shareUrl.value
	};
	if (navigator?.share && navigator?.canShare(data)) {
		try {
			await navigator.share(data);
			trackSharePropertyClicked({
				share_whatsapp: false,
				share_facebook: false,
				copy_link: false,
				share_email: false,
				share_twitter: false,
				share_instagram: false,
				share_clicked: true,
				[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(props.property)
			});
		} catch {
			return;
		}
	} else {
		await copy(shareUrl.value);
		toast({
			description: h(CopyToastDescription)
		});
		trackSharePropertyClicked({
			share_whatsapp: false,
			share_facebook: false,
			copy_link: true,
			share_email: false,
			share_twitter: false,
			share_instagram: false,
			share_clicked: true,
			[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(props.property)
		});
	}
}

const centerCoordinates = computed<{ lat: number; lng: number }>(() => ({
	lat: props.property?.h3Cluster?.geometry?.coordinates[1] ?? 0,
	lng: props.property?.h3Cluster?.geometry?.coordinates[0] ?? 0
}));

function handleGoBack() {
	if (isCarouselOpen.value) {
		isCarouselOpen.value = false;
		return;
	}

	isOpen.value = false;
}

function showCarousel(imageIndex: number) {
	currentCarouselItemIndex.value = imageIndex;
	isCarouselOpen.value = true;
}

async function scrollToMosaicImage(imageIndex: number) {
	await until(imagesEl).toBeTruthy();
	await nextTick();

	const scrollContainer = imagesEl.value;
	const imageElement = scrollContainer?.querySelector<HTMLElement>(
		`[data-photo-index="${imageIndex}"]`
	);

	if (!scrollContainer || !imageElement) return;

	imageElement.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
		inline: 'nearest'
	});
}

async function restoreImagesScrollPosition() {
	if (currentTab.value !== 'images') {
		currentTab.value = 'images';
	}

	await until(imagesEl).toBeTruthy();
	imagesScrollPosition.value = lastImagesScrollPosition.value;
	shouldRestoreImagesScroll.value = false;
}

watch(isOpen, async newValue => {
	if (!newValue) {
		// Modal is closing - reset to defaults
		isCarouselOpen.value = false;
		currentCarouselItemIndex.value = 0;
		currentTab.value = 'images';
	} else {
		if (
			props.initialPhotoIndex !== undefined &&
			currentTab.value === 'images'
		) {
			currentCarouselItemIndex.value = props.initialPhotoIndex;

			if (!isCarouselOpen.value) {
				if (shouldRestoreImagesScroll.value) {
					await restoreImagesScrollPosition();
				} else {
					await scrollToMosaicImage(props.initialPhotoIndex);
				}
			}
		}
	}
});

watch(isCarouselOpen, async (newValue, oldValue) => {
	if (newValue && !oldValue) {
		if (!isOpen.value) return;

		lastImagesScrollPosition.value =
			imagesEl.value?.scrollTop ?? imagesScrollPosition.value;
		return;
	}

	if (!oldValue) return;

	shouldRestoreImagesScroll.value = true;

	if (isOpen.value) {
		await restoreImagesScrollPosition();
	}
});

watch(
	() => props.initialPhotoIndex,
	(newValue, oldValue) => {
		if (!isOpen.value && newValue !== oldValue) {
			shouldRestoreImagesScroll.value = false;
		}
	}
);

// Debounced function to load similar properties (for subsequent calls)
const debouncedLoadSimilarProperties = useDebounceFn(() => {
	if (
		currentTab.value === 'similar' &&
		similarProperties.value.length === 0 &&
		!isSimilarPropertiesLoading.value &&
		basePropertyRef.value
	) {
		loadSimilarProperties();
	}
}, 150);

// Track if this is the first time accessing similar tab
const hasAccessedSimilarTab = ref(false);

// Watch for tab changes to trigger similar properties loading
watch(currentTab, (newTab: Tab) => {
	if (newTab === 'similar') {
		// First time accessing similar tab - load immediately
		if (!hasAccessedSimilarTab.value) {
			hasAccessedSimilarTab.value = true;

			if (
				similarProperties.value.length === 0 &&
				!isSimilarPropertiesLoading.value &&
				basePropertyRef.value
			) {
				nextTick(() => {
					loadSimilarProperties();
				});
			}
		} else {
			// Subsequent accesses - use debounced function
			nextTick(() => {
				debouncedLoadSimilarProperties();
			});
		}
	}
});

function handleTabClicked(tabId: Tab) {
	if (tabId === 'similar') {
		trackCtaSimilarPropertiesClicked({
			[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
			[MixpanelProps.CLICK_CTA_FLOATING]: false,
			[MixpanelProps.CLICK_CTA_FIXED]: true,
			[MixpanelProps.CTA_TEXT]: 'Semelhantes',
			[MixpanelProps.CURRENT_PAGE]: 'Detalhe do Imóvel'
		});
	}
}

function handleShareClicked(option: SharingOption) {
	trackSharePropertyClicked({
		share_whatsapp: option.label === 'WhatsApp',
		share_facebook: option.label === 'Facebook',
		copy_link: false,
		share_email: option.label === 'Via e-mail' || option.label === 'E-mail',
		share_twitter: option.label === 'Twitter',
		share_clicked: true,
		[MixpanelProps.VIDEO_SHORTS]: hasShortsVideo(props.property)
	});
}

function handlePhotoClick(image: PropertyImage, index: number) {
	showCarousel(index);
	const photoUrl = image.watermarkUrl || image.url;

	if (!photoUrl) return;

	emit('click-photo', {
		photoUrl: photoUrl,
		photoCount: index + 1 // Convert 0-based index to 1-based for consistency with tracking
	});
}

function handleVideoClick(videoUrl: string) {
	emit('click-video', videoUrl);
}

const shareContentRef = ref<HTMLElement | null>(null);
const shareContentButton = ref<HTMLElement | null>(null);
onClickOutside(shareContentRef, () => (showShareContent.value = false), {
	ignore: [shareContentButton]
});

const isZoomed = ref(false);
const updateThumbnailZoom = (zoomed: boolean) => {
	isZoomed.value = zoomed;
};

const thumbnailCarouselBaseClasses = ['flex', 'flex-col'];
const thumbnailCarouselZoomedClasses = ['h-full', 'md:h-[calc(100%-208px)]'];
const thumbnailCarouselDefaultClasses = ['h-[calc(100%-208px)]'];

const thumbnailCarouselClasses = computed(() => {
	const heightClasses = isZoomed.value
		? thumbnailCarouselZoomedClasses
		: thumbnailCarouselDefaultClasses;

	return cn(...thumbnailCarouselBaseClasses, ...heightClasses);
});

const agentFirstName = computed(
	() => props.property.agent.name.split(' ')[0] || ''
);

const propertyModalScrollContainer = ref<HTMLElement | null>(null);

const getImageAlt = (index: number | null) => {
	return getPropertyImageAlt(
		props.property.propertyType?.name,
		props.property.suites,
		props.property.region,
		props.property.city,
		props.property.askingPrice,
		index
	);
};

//Note: This snippet works only on the client side
const breakpoints = useBreakpoints(breakpointsTailwind);
const isLgLargeAndUp = breakpoints.greaterOrEqual('lg');
</script>

<template>
	<LazyClientOnly>
		<template #fallback>
			<span class="m-0 hidden p-0"></span>
		</template>
		<Modal
			v-model:open="isOpen"
			:show-close-icon="false"
			content-class="h-full max-w-screen max-h-screen flex flex-col bg-white"
			header-class="font-matter-sq text-xl md:text-2xl font-normal leading-[1.875rem] z-10 tracking-[0.02em] text-left text-primary justify-start items-start h-min pb-0 px-5 pt-5 md:p-5"
			footer-class="hidden"
			prevent-interact-outside
		>
			<template #header>
				<Container class="py-0! px-0">
					<DialogTitle
						:class="
							cn('flex size-full items-center md:pb-0', {
								'pb-5': isCarouselOpen
							})
						"
					>
						<div
							class="relative flex w-full items-start justify-start md:flex-nowrap md:items-center md:justify-center"
							:class="{ 'w-1/2 flex-wrap': !isCarouselOpen }"
						>
							<div
								:class="{
									'md:w-full': !isCarouselOpen
								}"
								class="order-1 flex w-1/2 items-center justify-between md:order-1 md:w-auto md:px-2"
							>
								<TextLink
									:element="'a'"
									class="flex items-center gap-2"
									@click="handleGoBack"
								>
									<ArrowLeftIcon class="size-5" />
									<span class="text-base font-normal">Voltar</span>
								</TextLink>
							</div>

							<div
								v-if="!isCarouselOpen"
								class="order-3 flex w-full md:order-2 md:justify-center"
							>
								<TabsNavigator
									v-model="currentTab"
									:tabs="tabs"
									class="flex gap-4"
									container-class="justify-start! md:justify-center!"
									@tab-clicked="handleTabClicked"
								/>
							</div>
							<div
								v-else
								:class="{
									'md:w-full': !isCarouselOpen,
									'w-full': isCarouselOpen
								}"
								class="order-2 flex grow justify-center px-10 font-matter-sq text-[18px] font-normal leading-[27px]"
							>
								{{ currentCarouselItemIndex + 1 }} de
								{{ property.images.length }}
								{{ grammarInflection(property.images.length, 'foto', 'fotos') }}
							</div>

							<div
								v-if="isCarouselOpen"
								class="order-3 block w-1/2 md:hidden"
							/>

							<div
								v-if="!isCarouselOpen"
								class="order-2 flex w-1/2 items-start justify-end md:order-3 md:w-full md:items-center"
							>
								<div class="flex flex-col items-start">
									<button
										ref="shareContentButton"
										class="z-10 hidden size-11 items-center justify-center rounded-full border border-white-dark-3 bg-white p-3 md:flex"
										@click="toggleShareContent"
									>
										<IconPilarSend48Degrees
											class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
										/>
									</button>

									<button
										class="z-10 flex size-11 items-center justify-center rounded-full border border-white-dark-3 bg-white p-3 md:hidden"
										@click="shareMobile"
									>
										<IconPilarSend48Degrees
											class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
										/>
									</button>
								</div>

								<div
									v-show="showShareContent"
									ref="shareContentRef"
									class="absolute -top-7 right-0 z-50 w-[125px] translate-y-[60%] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
								>
									<ShareContent
										v-model="showShareContent"
										variant="dropdown"
										mail-subject="Leia mais sobre este imóvel"
										:content-to-share="shareUrl"
										:share-analytics-extras="shareAnalyticsExtras"
										show-labels
										@share-clicked="handleShareClicked"
									/>
								</div>
							</div>
						</div>
					</DialogTitle>
				</Container>
			</template>
			<template #default>
				<Container
					v-if="!isCarouselOpen"
					:ref="
						isCurrentTab('similar') ? 'propertyModalScrollContainer' : undefined
					"
					:class="
						cn('flex grow py-5 md:pb-8 md:pt-6', {
							'p-0 md:pb-8 md:pt-6': isCurrentTab('map'),
							'p-0! md:px-5! md:py-0! overflow-y-auto relative z-0':
								isCurrentTab('similar'),
							'px-0! md:px-0!': isCarouselOpen
						})
					"
				>
					<div
						:class="
							cn('flex h-full justify-between gap-8', {
								'overflow-y-scroll pb-8': !isCurrentTab('similar')
							})
						"
					>
						<div
							class="size-full grow"
							:class="{
								'md:w-2/3': !isCarouselOpen && !isCurrentTab('similar'),
								'md:w-full': isCarouselOpen || isCurrentTab('similar')
							}"
						>
							<div
								v-if="isCurrentTab('images') && !isCarouselOpen"
								ref="imagesEl"
								class="grid h-[calc(100dvh-150px)] grid-cols-2 gap-4 overflow-y-auto pb-12"
							>
								<NuxtImg
									v-for="(image, index) in property.images"
									:key="`property-image-${image.watermarkUrl || image.url}}}`"
									:data-photo-index="index"
									decoding="async"
									loading="lazy"
									:src="image.watermarkUrl || image.url"
									:width="getImageDimensions(index).width"
									:sizes="getImageDimensions(index).sizes"
									densities="x1"
									format="webp"
									:placeholder="[50, 25]"
									:alt="getImageAlt(index)"
									:class="
										cn(
											getColSpanClass(index),
											'size-full cursor-pointer object-cover md:h-[320px]'
										)
									"
									@click="handlePhotoClick(image, index)"
								/>
							</div>
							<div
								v-if="isCurrentTab('videos')"
								class="flex h-[calc(100dvh-150px)] flex-col gap-5 overflow-y-auto pb-10 md:gap-6"
							>
								<VideoPlayer
									v-for="video in property.videos"
									:key="`property-video-${video}`"
									:src="video"
									class="aspect-video flex-1"
									@play="handleVideoClick"
								/>
							</div>
							<div v-if="isCurrentTab('map')" class="h-full">
								<ClientOnly fallback-tag="div">
									<template #fallback>
										<div
											class="relative size-full bg-map-placeholder-image bg-cover bg-no-repeat"
										/>
									</template>

									<GmapMap
										:center="centerCoordinates"
										:zoom="17"
										:min-zoom="16"
										:max-zoom="17"
										:zoom-control="isLgLargeAndUp"
										:draggable="true"
										class="size-full"
									>
										<GmapClusterRadius
											v-if="property?.h3Cluster"
											:h3-cluster="property.h3Cluster"
											:is-selected="true"
											:is-clickable="false"
										/>
									</GmapMap>
								</ClientOnly>
							</div>
							<div v-if="isCurrentTab('similar')" class="relative z-0 h-full">
								<SimilarPropertiesList
									:properties="similarProperties"
									:is-loading="isSimilarPropertiesLoading"
									:has-more="hasSimilarPropertiesMore"
									:is-empty="shouldShowSimilarPropertiesEmptyState"
									:base-property="property"
									:scroll-container="propertyModalScrollContainer"
									@load-more="loadSimilarPropertiesNextPage"
								/>
							</div>
						</div>
						<div
							v-show="!isCarouselOpen && !isCurrentTab('similar')"
							class="hidden h-fit w-1/3 min-w-[400px] md:block md:min-w-[350px]"
						>
							<PropertyPrices
								:asking-price="property.askingPrice"
								:condo-fee="property.condoFee"
								:condo-name="property.condoName"
								:tax="property.tax"
								:agent="property.agent"
								:company-name="property.company.name"
								:company-logo="property.company.smallLogo"
								:launch-year="launchYear"
								:property-type-name="property.propertyType?.name"
								:property-type="property.propertyType"
								:property-id="property.id"
								:commercial-id="property.commercialId"
								:region="property.region"
								:section-name="sectionName"
								@open-contact-modal="
									() => {
										emit('click-fixed-contact-button');
									}
								"
							/>
						</div>
					</div>
				</Container>
				<div v-else :class="thumbnailCarouselClasses">
					<ThumbnailCarousel
						v-model:selected-index="currentCarouselItemIndex"
						:images="property.images"
						:alt-callback="getImageAlt"
						@update-zoom="updateThumbnailZoom"
					/>
				</div>
				<FixedFooterContact
					v-if="!isCarouselOpen && !isCurrentTab('similar')"
					:agent="property.agent"
					:company-logo="property.company.smallLogo"
					:company-name="property.company.name"
					:is-exclusive="false"
					:property-id="property.id"
					:commercial-id="property.commercialId"
					:in-user-wishlist="property.inUserWishlist"
					@click-fixed-footer-contact="emit('click-fixed-contact-button')"
				/>
			</template>
		</Modal>
	</LazyClientOnly>
</template>
